"use client";

import React from "react";
import { Shield, CircleCheck, Lock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SecureYourInfrastructure() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="32247" id="ready-to-restore-your-caravan" className="dark bg-primary text-primary-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl translate-y-1/2" />
        <Shield className="absolute -right-12 top-1/2 -translate-y-1/2 w-[600px] h-[600px] text-accent opacity-[0.03] rotate-12" strokeWidth={0.5} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {}
          <div ref={ref} className={`space-y-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-mono font-medium uppercase tracking-wider">
                <Lock className="w-3 h-3" />
                <span>Expert Restoration Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-sans font-extrabold tracking-tighter text-white">
                Ready to Restore Your <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent">
                  Caravan Today?
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl font-sans">
                Based in South Windsor, our specialists handle everything from 
                insurance hail damage to custom structural upgrades. Get your 
                home-on-wheels back to pristine condition.
              </p>
            </div>

            <div className="space-y-4">
              {["Insurance & private smash repairs", "Specialist hail damage restoration", "Electrical & plumbing installations", "Structural & chassis reinforcements"].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center">
                    <CircleCheck className="w-4 h-4 text-secondary" />
                  </div>
                  <span className="text-foreground/90 font-sans">{item}</span>
                </div>)}
            </div>
          </div>

          {}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative group">
              {}
              <div className="absolute -inset-[1px] bg-gradient-to-br from-secondary to-accent rounded-xl opacity-70 blur-[2px] group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative bg-card/95 backdrop-blur-xl rounded-xl p-8 md:p-10 border border-white/5 shadow-2xl">
                <div className="mb-8">
                  <h3 className="text-2xl font-sans font-bold text-white mb-2">
                    Start Your Quote
                  </h3>
                  <p className="text-sm text-muted-foreground font-sans">
                    Fill out the details below to schedule your professional assessment.
                  </p>
                </div>

                <FormProvider formKey="repair_quote_form_home" formId="repair_quote_form_home" sectionName="ready-to-restore-your-caravan" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                infrastructure_type: "",
                security_concerns: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                company: {
                  required: true
                },
                infrastructure_type: {
                  required: true
                }
              }} submitText="Get My Quote" successMessage="Quote request received. Our repair team will contact you shortly." errorMessage="Submission failed. Please call us directly.">
                  <SecurityAssessmentForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function SecurityAssessmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mb-2">
          <Shield className="w-8 h-8 text-secondary" />
        </div>
        <h4 className="text-xl font-sans font-semibold text-white">Request Received</h4>
        <p className="text-muted-foreground font-sans">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary font-sans" onClick={() => window.location.reload()}>
          Submit Another Request
        </Button>
      </div>;
  }
  const inputClasses = "bg-slate-900/50 border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-secondary transition-colors placeholder:text-muted-foreground/50 h-12 font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">
                Caravan Make/Model
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Jayco Journey" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">
                Contact Number <span className="text-xs opacity-50">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0400 000 000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="infrastructure_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">
              Service Required
            </FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className={`${inputClasses} w-full appearance-none text-sm text-foreground bg-transparent cursor-pointer`}>
                  <option value="" disabled className="bg-card text-muted-foreground">
                    Select repair type
                  </option>
                  {["Smash Repair", "Hail Damage", "Insurance Claim", "Structural Work", "Electrical/Plumbing"].map((opt, i) => <option key={i} value={opt} className="bg-card text-foreground" data-index={i}>
                      {opt}
                    </option>)}
                </select>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="security_concerns" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">
              Additional Details <span className="text-xs opacity-50">(Optional)</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the damage or required installations..." {...field} className={`${inputClasses} min-h-[100px] resize-none pt-3 font-sans`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-sans font-bold bg-accent text-accent-foreground hover:opacity-90 border-0 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:shadow-[0_6px_30px_rgba(249,115,22,0.5)] transition-all duration-300 mt-4 rounded-sm uppercase tracking-wider">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            Processing...
          </span> : "Get My Quote"}
      </Button>
    </Form>;
}